import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar, Repeat, Clock, MessageSquare, Tag } from 'lucide-react';

/**
 * OrderNotesCard — iter-230d
 *
 * Renders a single "Order Notes" card below the Order Items card on the
 * admin Order Details page. Surfaces order-level metadata that was
 * previously hidden inside the raw Mongo document:
 *
 *   • Subscription details (selling plan, start date, billing cycle,
 *     delivery frequency, delivery days, next billing date) — joined
 *     from `customer_subscriptions` by the backend.
 *   • Rental schedule (`rental_info.start_date` / `end_date` / `days`).
 *   • Booking schedule (`booking_info.date` / `time`).
 *   • Shopify customer note (the free-text note customer types at
 *     checkout — e.g. "leave with security", "ring twice").
 *   • Order-level tags.
 *
 * Returns `null` if none of the above are present so regular product
 * orders see a clean Order Items section with no empty trailing card.
 */
const fmtDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
};

const humanise = (slug) => {
  if (!slug) return slug;
  return String(slug)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function OrderNotesCard({ order }) {
  const sub = order?.subscription_info || null;
  const rental = order?.rental_info || null;
  const booking = order?.booking_info || null;
  const shopifyNote = order?.shopify_note || order?.notes || '';
  const tags = Array.isArray(order?.tags)
    ? order.tags.filter((t) => t && String(t).trim())
    : [];

  const hasContent =
    sub ||
    (rental && (rental.start_date || rental.end_date)) ||
    (booking && (booking.date || booking.time)) ||
    (shopifyNote && String(shopifyNote).trim()) ||
    tags.length > 0;

  if (!hasContent) return null;

  return (
    <Card data-testid="order-notes-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-amber-600" />
          Order Notes
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {sub && (
          <section data-testid="order-notes-subscription">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
              <Repeat className="h-4 w-4" />
              Subscription
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {sub.selling_plan_name && (
                <div>
                  <p className="text-gray-500 text-xs">Plan</p>
                  <p className="font-medium">{sub.selling_plan_name}</p>
                </div>
              )}
              {sub.start_date && (
                <div>
                  <p className="text-gray-500 text-xs">Start date</p>
                  <p className="font-medium">{fmtDate(sub.start_date)}</p>
                </div>
              )}
              {sub.billing_cycle && (
                <div>
                  <p className="text-gray-500 text-xs">Billing cycle</p>
                  <p className="font-medium">{humanise(sub.billing_cycle)}</p>
                </div>
              )}
              {sub.delivery_frequency && (
                <div>
                  <p className="text-gray-500 text-xs">Delivery frequency</p>
                  <p className="font-medium">{humanise(sub.delivery_frequency)}</p>
                </div>
              )}
              {Array.isArray(sub.delivery_days) && sub.delivery_days.length > 0 && (
                <div>
                  <p className="text-gray-500 text-xs">Delivery days</p>
                  <p className="font-medium">{sub.delivery_days.map(humanise).join(', ')}</p>
                </div>
              )}
              {sub.next_billing_date && (
                <div>
                  <p className="text-gray-500 text-xs">Next billing</p>
                  <p className="font-medium">{fmtDate(sub.next_billing_date)}</p>
                </div>
              )}
              {sub.status && (
                <div>
                  <p className="text-gray-500 text-xs">Status</p>
                  <p className="font-medium capitalize">{sub.status}</p>
                </div>
              )}
              {typeof sub.deliveries_completed === 'number' && (
                <div>
                  <p className="text-gray-500 text-xs">Deliveries completed</p>
                  <p className="font-medium">{sub.deliveries_completed}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {rental && (rental.start_date || rental.end_date || rental.days) && (
          <section data-testid="order-notes-rental">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Rental schedule
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              {rental.start_date && (
                <div>
                  <p className="text-gray-500 text-xs">Start</p>
                  <p className="font-medium">{fmtDate(rental.start_date)}</p>
                </div>
              )}
              {rental.end_date && (
                <div>
                  <p className="text-gray-500 text-xs">End</p>
                  <p className="font-medium">{fmtDate(rental.end_date)}</p>
                </div>
              )}
              {rental.days && (
                <div>
                  <p className="text-gray-500 text-xs">Duration</p>
                  <p className="font-medium">{rental.days} day{rental.days === 1 ? '' : 's'}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {booking && (booking.date || booking.time) && (
          <section data-testid="order-notes-booking">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Booking
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {booking.date && (
                <div>
                  <p className="text-gray-500 text-xs">Date</p>
                  <p className="font-medium">{fmtDate(booking.date)}</p>
                </div>
              )}
              {booking.time && (
                <div>
                  <p className="text-gray-500 text-xs">Time</p>
                  <p className="font-medium">{booking.time}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {shopifyNote && String(shopifyNote).trim() && (
          <section data-testid="order-notes-customer-note">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Customer note
            </h4>
            <blockquote className="text-sm text-gray-700 bg-amber-50 border-l-4 border-amber-300 pl-3 py-2 whitespace-pre-wrap rounded">
              {shopifyNote}
            </blockquote>
          </section>
        )}

        {tags.length > 0 && (
          <section data-testid="order-notes-tags">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Tags
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t, idx) => (
                <span
                  key={`${t}-${idx}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200"
                >
                  {String(t).trim()}
                </span>
              ))}
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
